import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  ThreadMetadata,
  IntermediateStep,
  PendingInterrupt,
  StreamingStatus,
} from '@/types/langgraph';
import type { CurrentTeam, TeamLeader, TeamMember } from '@/types/onboarding.types';

export interface ComponentMetadata {
  name: string;
  props?: Record<string, any>;
  replaceMessage?: boolean;
  messageId: string;
}

/**
 * Custom event data structure for storing latest event
 */
export interface CustomEventData {
  type: 'ui' | 'phase_update' | 'current_team_update' | 'team_leader_update' | 'mcp_app';
  timestamp: number;
  // Common fields for both UI and phase_update events
  id?: string;
  name?: string;
  props?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: number;
  isStreaming?: boolean;
  isError?: boolean;
  errorMessage?: string;
  component?: ComponentMetadata;
  /** Base64 data URI for an image attached to this message (user messages only) */
  imageDataUri?: string;
}

export interface ChatState {
  messages: ChatMessage[];
  isTyping: boolean;
  currentThreadId: string | null;
  threads: ThreadMetadata[];
  threadLoading: boolean;
  streamingStatus: StreamingStatus;
  intermediateSteps: IntermediateStep[];
  showIntermediateSteps: boolean;
  currentRunId: string | null;
  pendingInterrupt: PendingInterrupt | null;
  lastError: string | null;
  values: CustomEventData[];
  currentTeam: CurrentTeam | null;
  teamLeader: TeamLeader | null;
  teamMembers: TeamMember[];
}

const initialState: ChatState = {
  messages: [],
  isTyping: false,
  currentThreadId: null,
  threads: [],
  threadLoading: false,
  streamingStatus: 'idle',
  intermediateSteps: [],
  showIntermediateSteps: false,
  currentRunId: null,
  pendingInterrupt: null,
  lastError: null,
  values: [],
  currentTeam: null,
  teamLeader: null,
  teamMembers: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessage: (
      state,
      action: PayloadAction<{
        id: string;
        content: string;
        isStreaming?: boolean;
        isError?: boolean;
        errorMessage?: string;
        component?: ComponentMetadata;
      }>
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) {
        message.content = action.payload.content;
        if (action.payload.isStreaming !== undefined) {
          message.isStreaming = action.payload.isStreaming;
        }
        if (action.payload.isError !== undefined) {
          message.isError = action.payload.isError;
        }
        if (action.payload.errorMessage !== undefined) {
          message.errorMessage = action.payload.errorMessage;
        }
        if (action.payload.component !== undefined) {
          message.component = action.payload.component;
        }
      } else {
        console.warn('⚠️ Redux: Message not found for update:', action.payload.id);
      }
    },
    addOrUpdateMessage: (
      state,
      action: PayloadAction<{
        id: string;
        content: string;
        role?: 'user' | 'assistant';
        isStreaming?: boolean;
        isError?: boolean;
        errorMessage?: string;
        component?: ComponentMetadata;
      }>
    ) => {
      const existingMessage = state.messages.find((m) => m.id === action.payload.id);

      if (existingMessage) {
        // Update existing message
        existingMessage.content = action.payload.content;
        if (action.payload.isStreaming !== undefined) {
          existingMessage.isStreaming = action.payload.isStreaming;
        }
        if (action.payload.isError !== undefined) {
          existingMessage.isError = action.payload.isError;
        }
        if (action.payload.errorMessage !== undefined) {
          existingMessage.errorMessage = action.payload.errorMessage;
        }
        if (action.payload.component !== undefined) {
          existingMessage.component = action.payload.component;
        }
      } else {
        // Add new message
        state.messages.push({
          id: action.payload.id,
          role: action.payload.role || 'assistant',
          content: action.payload.content,
          timestamp: Date.now(),
          isStreaming: action.payload.isStreaming,
          isError: action.payload.isError,
          errorMessage: action.payload.errorMessage,
          component: action.payload.component,
        });
      }
    },
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.intermediateSteps = [];
    },
    loadMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setCurrentThread: (state, action: PayloadAction<string | null>) => {
      state.currentThreadId = action.payload;
      // Also save to sessionStorage for use in other parts of the app
      if (action.payload) {
        sessionStorage.setItem('THREAD_ID', action.payload);
      } else {
        sessionStorage.removeItem('THREAD_ID');
      }
    },
    setThreads: (state, action: PayloadAction<ThreadMetadata[]>) => {
      state.threads = action.payload;
    },
    addThread: (state, action: PayloadAction<ThreadMetadata>) => {
      state.threads.unshift(action.payload);
    },
    removeThread: (state, action: PayloadAction<string>) => {
      state.threads = state.threads.filter((t) => t.id !== action.payload);
    },
    setThreadLoading: (state, action: PayloadAction<boolean>) => {
      state.threadLoading = action.payload;
    },
    setStreamingStatus: (state, action: PayloadAction<StreamingStatus>) => {
      state.streamingStatus = action.payload;
      if (action.payload === 'streaming') {
        state.isTyping = true;
      } else if (
        action.payload === 'completed' ||
        action.payload === 'error' ||
        action.payload === 'idle'
      ) {
        state.isTyping = false;
      }
    },
    setCurrentRunId: (state, action: PayloadAction<string | null>) => {
      state.currentRunId = action.payload;
    },
    addIntermediateStep: (state, action: PayloadAction<IntermediateStep>) => {
      state.intermediateSteps.push(action.payload);
    },
    clearIntermediateSteps: (state) => {
      state.intermediateSteps = [];
    },
    toggleShowIntermediateSteps: (state) => {
      state.showIntermediateSteps = !state.showIntermediateSteps;
    },
    setShowIntermediateSteps: (state, action: PayloadAction<boolean>) => {
      state.showIntermediateSteps = action.payload;
    },
    setPendingInterrupt: (state, action: PayloadAction<PendingInterrupt | null>) => {
      state.pendingInterrupt = action.payload;
      if (action.payload) {
        state.streamingStatus = 'paused';
      }
    },
    clearPendingInterrupt: (state) => {
      state.pendingInterrupt = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.lastError = action.payload;
      state.streamingStatus = 'error';
      state.isTyping = false;
    },
    clearError: (state) => {
      state.lastError = null;
    },
    addCustomEventValue: (state, action: PayloadAction<CustomEventData>) => {
      state.values.push(action.payload);
    },
    setCustomEventValues: (state, action: PayloadAction<CustomEventData[]>) => {
      state.values = action.payload;
    },
    clearCustomEventValues: (state) => {
      state.values = [];
    },
    setCurrentTeam: (state, action: PayloadAction<CurrentTeam | null>) => {
      state.currentTeam = action.payload;
    },
    setTeamLeader: (state, action: PayloadAction<TeamLeader | null>) => {
      state.teamLeader = action.payload;
    },
    setTeamMembers: (state, action: PayloadAction<TeamMember[]>) => {
      state.teamMembers = action.payload;
    },
  },
});

export const {
  addMessage,
  updateMessage,
  addOrUpdateMessage,
  setTyping,
  clearMessages,
  loadMessages,
  setCurrentThread,
  setThreads,
  addThread,
  removeThread,
  setThreadLoading,
  setStreamingStatus,
  setCurrentRunId,
  addIntermediateStep,
  clearIntermediateSteps,
  toggleShowIntermediateSteps,
  setShowIntermediateSteps,
  setPendingInterrupt,
  clearPendingInterrupt,
  setError,
  clearError,
  addCustomEventValue,
  setCustomEventValues,
  clearCustomEventValues,
  setCurrentTeam,
  setTeamLeader,
  setTeamMembers,
} = chatSlice.actions;

export default chatSlice.reducer;
